import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import { doc, getDoc, runTransaction, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { PendingSaleOverrideSettings } from '@otw/models';
import {
  decidePendingSaleOverrideConsume,
  normalizePendingSaleOverrideCode,
  PendingSaleOverrideConsumeError,
} from './pendingSaleOverrideCode';

interface UsePendingSaleOverrideSettingsOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  dontShowUpdates?: boolean;
  enabled?: boolean;
  /** When true, create a code document if one does not exist yet. */
  ensureCode?: boolean;
}

const SETTINGS_COLLECTION = 'pending_sale_override_settings';
const SETTINGS_DOC_ID = 'default';

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function generatePendingSaleOverrideCode(length = 6): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

export const usePendingSaleOverrideSettings = (
  options?: UsePendingSaleOverrideSettingsOptions,
) => {
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  const queryKey = ['pending_sale_override_settings'];
  const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);

  const {
    data: pendingSaleOverrideSettings,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<PendingSaleOverrideSettings | null> => {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as PendingSaleOverrideSettings;
      }

      if (!options?.ensureCode) {
        return null;
      }

      const now = new Date().toISOString();
      const code = generatePendingSaleOverrideCode();
      const settingsData = {
        code,
        created_at: now,
        updated_at: now,
        created_by: currentUser?.uid || '',
        updated_by: currentUser?.uid || '',
      };
      await setDoc(docRef, settingsData, { merge: true });
      return { id: SETTINGS_DOC_ID, ...settingsData };
    },
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled !== false,
  });

  const updatePendingSaleOverrideSettings = useMutation({
    mutationFn: async ({ data }: { data: Partial<Pick<PendingSaleOverrideSettings, 'code'>> }) => {
      const now = new Date().toISOString();
      const existingDoc = await getDoc(docRef);
      const existingData = existingDoc.exists() ? existingDoc.data() : null;

      const settingsData = {
        ...(existingData || {}),
        ...data,
        updated_at: now,
        updated_by: currentUser?.uid || '',
        ...(existingData
          ? {}
          : {
              created_at: now,
              created_by: currentUser?.uid || '',
            }),
      };

      await setDoc(docRef, settingsData, { merge: true });
      return {
        id: SETTINGS_DOC_ID,
        ...settingsData,
      } as PendingSaleOverrideSettings;
    },
    onSuccess: (updatedSettings) => {
      if (!options?.dontShowUpdates) {
        showSuccess('Pending sale override code updated');
      }

      queryClient.setQueryData(queryKey, updatedSettings);
      options?.onSuccess?.();
    },
    onError: (err) => {
      if (!options?.dontShowUpdates) {
        showError('Error updating pending sale override code');
      }

      console.error('Error updating pending sale override settings:', err);
      options?.onError?.(err as Error);
    },
  });

  const regenerateCode = async () => {
    const code = generatePendingSaleOverrideCode();
    return updatePendingSaleOverrideSettings.mutateAsync({ data: { code } });
  };

  const consumeCodeMutation = useMutation({
    mutationFn: async (enteredCode: string) => {
      const uid = currentUser?.uid || '';
      return runTransaction(db, async (transaction) => {
        const snap = await transaction.get(docRef);
        const existing = snap.exists() ? snap.data() : null;
        const storedCode = existing ? String(existing.code ?? '') : '';
        let candidate = generatePendingSaleOverrideCode();
        for (
          let attempt = 0;
          attempt < 5 &&
          normalizePendingSaleOverrideCode(candidate) ===
            normalizePendingSaleOverrideCode(storedCode);
          attempt += 1
        ) {
          candidate = generatePendingSaleOverrideCode();
        }

        const decision = decidePendingSaleOverrideConsume({
          storedCode,
          enteredCode,
          nextCode: candidate,
        });
        if (!decision.ok) {
          throw new PendingSaleOverrideConsumeError(decision.reason);
        }

        const now = new Date().toISOString();
        const settingsData = {
          ...(existing || {}),
          code: decision.nextCode,
          updated_at: now,
          updated_by: uid,
          ...(existing
            ? {}
            : {
                created_at: now,
                created_by: uid,
              }),
        };
        transaction.set(docRef, settingsData, { merge: true });
        return {
          id: SETTINGS_DOC_ID,
          ...settingsData,
        } as PendingSaleOverrideSettings;
      });
    },
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(queryKey, updatedSettings);
    },
  });

  return {
    pendingSaleOverrideSettings: pendingSaleOverrideSettings || null,
    code: pendingSaleOverrideSettings?.code || null,
    isLoading,
    isError,
    error,
    updatePendingSaleOverrideSettings: updatePendingSaleOverrideSettings.mutate,
    updatePendingSaleOverrideSettingsAsync: updatePendingSaleOverrideSettings.mutateAsync,
    regenerateCode,
    consumeCode: consumeCodeMutation.mutateAsync,
    isUpdating: updatePendingSaleOverrideSettings.isPending,
    isConsuming: consumeCodeMutation.isPending,
    refetch,
  };
};
